import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

import { checkPostcode } from "@/lib/postcode";

const CreateJobSchema = z.object({
  category_id: z.string().uuid(),
  title: z.string().trim().min(4).max(120),
  description: z.string().trim().min(10).max(2000),
  postcode: z.string().trim().refine((v) => checkPostcode(v).ok, "We currently only cover Greater London — enter a London postcode."),
  scheduled_at: z.string().datetime().optional().nullable(),
  flexible_timing: z.boolean().default(false),
  urgency: z.enum(["low", "normal", "high"]).default("normal"),
  budget_min: z.number().min(0).max(10000).optional().nullable(),
  budget_max: z.number().min(0).max(10000).optional().nullable(),
  // Storage paths inside the private `job-photos` bucket OR fully-qualified URLs.
  photos: z.array(z.string().min(1).max(512)).max(8).optional().default([]),
});

export const createJobAndMatch = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => CreateJobSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: job, error: jobErr } = await supabase
      .from("jobs")
      .insert({
        customer_id: userId,
        category_id: data.category_id,
        title: data.title,
        description: data.description,
        raw_input: data.description,
        postcode: data.postcode.toUpperCase(),
        scheduled_at: data.scheduled_at ?? null,
        flexible_timing: data.flexible_timing,
        urgency: data.urgency,
        budget_min: data.budget_min ?? null,
        budget_max: data.budget_max ?? null,
        photos: data.photos ?? [],
        status: "open",
      })
      .select("id, title, postcode, status, created_at")
      .single();

    if (jobErr || !job) {
      throw new Error(jobErr?.message ?? "Could not create job");
    }

    // Intelligent matching — score available helpers offering this category.
    // RLS allows public reads on helper_profiles where available=true; we run as the
    // user (RLS-scoped) and the matching is a best-effort suggestion list.
    const { data: helpers } = await supabase
      .from("helper_profiles")
      .select(
        "id, hourly_rate, avg_rating, rating_count, jobs_completed, response_time_minutes, instant_book_enabled, base_postcode, available, verification_status, helper_skills!inner(category_id)"
      )
      .eq("available", true)
      .eq("helper_skills.category_id", data.category_id)
      .limit(50);

    const postcodeArea = data.postcode.toUpperCase().split(/\s|(?=\d)/)[0] ?? "";

    // Retuned weights (2026-06): prioritise response speed, location proximity,
    // and verification status over raw job volume — these are the strongest
    // predictors of time-to-match and customer satisfaction.
    const ranked = (helpers ?? [])
      .map((h: any) => {
        const ratingScore = (Number(h.avg_rating) || 0) * 20; // 0-100
        const completedScore = Math.min(Number(h.jobs_completed) || 0, 100); // 0-100
        // Speed: <=10m=100, <=30m=80, <=60m=60, <=240m=35, else 15, unknown=40
        const rt = h.response_time_minutes == null ? null : Number(h.response_time_minutes);
        const responseScore =
          rt == null ? 40 :
          rt <= 10 ? 100 :
          rt <= 30 ? 80 :
          rt <= 60 ? 60 :
          rt <= 240 ? 35 : 15;
        const locationScore = h.base_postcode?.toUpperCase().startsWith(postcodeArea) ? 100 : 35;
        const verifiedBoost = h.verification_status === "verified" ? 40 : 0;
        const instantBoost = h.instant_book_enabled ? 12 : 0;
        const score =
          responseScore   * 0.30 +
          locationScore   * 0.25 +
          ratingScore     * 0.20 +
          completedScore  * 0.10 +
          verifiedBoost +
          instantBoost;
        return { helper_id: h.id, score, hourly_rate: Number(h.hourly_rate) };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    // Persist offers so the first-accept-wins flow can run. Insert via admin
    // client to bypass RLS (we've already validated the customer + job).
    if (ranked.length > 0) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      await supabaseAdmin.from("job_offers").insert(
        ranked.map((r) => ({ job_id: job.id, helper_id: r.helper_id, score: r.score })),
      );
      await supabaseAdmin.from("notifications").insert(
        ranked.map((r) => ({
          user_id: r.helper_id,
          type: "job_offer",
          title: "New job offer",
          body: job.title,
          link: "/helper/offers",
          data: { job_id: job.id },
        })),
      );
    }

    return { job, matches: ranked, matched_count: ranked.length };
  });

export const listMyJobs = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("jobs")
      .select("id, title, postcode, status, scheduled_at, urgency, created_at, category_id")
      .eq("customer_id", userId)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return { jobs: data ?? [] };
  });
